import ChargeHelper from 'features/Charge/chargeHelper';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTBSDropdown from 'general/components/OtherKeenComponents/KTBSDropdown';
import KTDropdownFilter from 'general/components/OtherKeenComponents/KTDropdownFilter';
import CustomDropdown2 from 'general/components/Others/CustomDropdown2';
import AppColors from 'general/constants/AppColors';
import AppConfigs from 'general/constants/AppConfigs';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';

ChargePointListFilterView.propTypes = {
    isInDropdown: PropTypes.bool,
    chargePointListFilter: PropTypes.object,
    selectedChargeStation: PropTypes.object,
    setSelectedChargeStation: PropTypes.func,
    updateFilter: PropTypes.func,
    setFilterDropdown: PropTypes.func,
    filterDropdown: PropTypes.object,
};

function ModelTreeDropdown({ value, onChange, brandList, modelList }) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [expandedBrands, setExpandedBrands] = useState({});
    const ref = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (brandList.length > 0) {
            const expanded = {};
            brandList.forEach((b) => { expanded[b.brandId] = true; });
            setExpandedBrands(expanded);
        }
    }, [brandList]);

    const groupedModels = useMemo(() => _.groupBy(modelList, 'brandId'), [modelList]);

    const filteredBrands = useMemo(() => {
        if (!search) return brandList;
        return brandList.filter((brand) =>
            (groupedModels[brand.brandId] ?? []).some((m) =>
                m.name?.toLowerCase().includes(search.toLowerCase())
            )
        );
    }, [brandList, groupedModels, search]);

    const selectedModel = modelList.find((m) => m.modelId === value);

    const toggleBrand = (brandId) => {
        setExpandedBrands((prev) => ({ ...prev, [brandId]: !prev[brandId] }));
    };

    const handleSelect = (modelId) => {
        onChange(modelId);
        setIsOpen(false);
        setSearch('');
    };

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button
                className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                type="button"
                onClick={() => setIsOpen((v) => !v)}
            >
                {selectedModel?.name ?? 'Tất cả'}
            </button>
            {isOpen && (
                <div
                    className="dropdown-menu show w-100 py-2"
                    style={{ maxHeight: '40vh', overflowY: 'auto', zIndex: 1050, minWidth: 260 }}
                >
                    <div className="mx-3 my-1 search-form-border position-sticky" style={{ top: 0, background: '#fff', zIndex: 1 }}>
                        <input
                            className="form-control"
                            placeholder="Tìm kiếm..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            autoFocus
                        />
                    </div>
                    <div
                        className="d-flex align-items-center justify-content-between dropdown-item px-5 cursor-pointer"
                        onClick={() => handleSelect('')}
                        style={{ paddingTop: 5, paddingBottom: 5 }}
                    >
                        <span style={{ fontSize: 13, fontWeight: 500, color: '#494968' }}>Tất cả</span>
                        {!value && <i className="fa-regular fa-check text-success" />}
                    </div>
                    {filteredBrands.map((brand) => {
                        const models = (groupedModels[brand.brandId] ?? []).filter(
                            (m) => !search || m.name?.toLowerCase().includes(search.toLowerCase())
                        );
                        if (models.length === 0) return null;
                        const isExpanded = expandedBrands[brand.brandId] !== false;
                        return (
                            <div key={brand.brandId}>
                                <div
                                    className="dropdown-item px-5 d-flex align-items-center cursor-pointer gap-2"
                                    onClick={() => toggleBrand(brand.brandId)}
                                    style={{ paddingTop: 5, paddingBottom: 5 }}
                                >
                                    <i
                                        className={`fa-solid fa-caret-${isExpanded ? 'down' : 'right'}`}
                                        style={{ fontSize: 14, color: '#494968' }}
                                    />
                                    <span style={{ fontSize: 13, fontWeight: 500, color: '#494968' }}>{brand.name}</span>
                                </div>
                                {isExpanded &&
                                    models.map((model) => (
                                        <div
                                            key={model.modelId}
                                            className="dropdown-item px-5 d-flex align-items-center justify-content-between cursor-pointer"
                                            style={{
                                                paddingTop: 5,
                                                paddingBottom: 5,
                                                background: value === model.modelId ? AppColors.gray[100] : undefined,
                                            }}
                                            onClick={() => handleSelect(model.modelId)}
                                        >
                                            <div className="d-flex align-items-center gap-2">
                                                <span style={{ width: 14, flexShrink: 0 }} />
                                                <i className="fa-solid fa-circle" style={{ fontSize: 6, color: '#494968' }} />
                                                <span style={{ fontSize: 13, fontWeight: 500, color: '#494968' }}>{model.name}</span>
                                            </div>
                                            {value === model.modelId && (
                                                <i className="fa-solid fa-check text-success" />
                                            )}
                                        </div>
                                    ))}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function ChargePointListFilterView({
    isInDropdown = true,
    chargePointListFilter,
    selectedChargeStation,
    setSelectedChargeStation,
    updateFilter,
    setFilterDropdown,
    filterDropdown,
}) {
    // MARK: ---- PROPERTIES ----
    const [brandList, setBrandList] = useState([]);
    const [modelList, setModelList] = useState([]);

    useEffect(() => {
        ChargeHelper.API.requestGetChargeBrandList({ limit: 9999 }).then((res) => {
            setBrandList(res.brands ?? []);
        });
        ChargeHelper.API.requestGetChargeModelList({ limit: 9999 }).then((res) => {
            setModelList(res.models ?? []);
        });
    }, []);

    const dropdownChargeStationEl = useMemo(() => {
        return (
            <CustomDropdown2
                id="stationIdEl"
                name="stationIdEl"
                toggleElement={
                    <button
                        className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between`}
                        type="button"
                    >
                        {selectedChargeStation?.name ?? 'Tất cả'}
                    </button>
                }
                selectedValue={selectedChargeStation}
                subTitleType="address"
                dropdownMenuItems={[{ name: 'Trạm sạc', role: 'STATION' }]}
                getSubItems={async (item) => {
                    return ChargeHelper.API.requestGetChargeStationList({
                        page: item?.page || 0,
                        limit: 10,
                        q: item?.q || '',
                    }).then((data) => {
                        return {
                            values: data.chargeStations?.map((station) => ({
                                id: station?.stationId,
                                name: station?.name,
                                item: station,
                                address: ChargeHelper.Utils.getChargeStationFullAddress(station),
                            })),
                            total: data.total,
                        };
                    });
                }}
                dropdownMenuStyle={{ maxHeight: '30vh' }}
                dropdownMenuClassName="overflow-auto w-100"
                onChange={(value) => {
                    setSelectedChargeStation(value);
                    if (isInDropdown) {
                        setFilterDropdown({ ...filterDropdown, stationId: value?.id });
                    } else {
                        updateFilter('stationId', value?.id);
                    }
                }}
                isDropdownForm={true}
                titleSearchable={true}
                searchable={true}
                searchFormClassName="mx-3 my-1 search-form-border"
                ignoreConfirmClick={true}
                defaultValue={{
                    name: 'Trạm sạc',
                    role: 'STATION',
                }}
                footerEl={
                    <div
                        className="p-4 position-sticky sticky-bottom bg-white"
                        style={{
                            borderTop: `1px solid ${AppColors.gray[300]}`,
                        }}
                    >
                        {UIHelper.buildNegativeButton('Bỏ chọn', () => {
                            if (isInDropdown) {
                                setFilterDropdown({ ...filterDropdown, stationId: '' });
                            } else {
                                updateFilter('stationId', '');
                            }
                            setSelectedChargeStation(null);
                        })}
                    </div>
                }
            />
        )
    }, [selectedChargeStation])

    // MARK: ---- METHODS ----
    const chargingSpeedOptions = [
        { value: '', name: 'Tất cả' },
        { value: 'NORMAL', name: 'Sạc thường: ≤ 11 kW' },
        { value: 'FAST', name: 'Sạc nhanh: 20 kW → 60 kW' },
        { value: 'SUPERFAST', name: 'Sạc siêu nhanh: ≥ 120kW' },
    ]
    return (
        <div>
            <div className={`${'col-12 p-0'}`}>
                <KTFormSearch
                    name="search-bar"
                    placeholder="Theo mã trụ sạc, mã OCPP, số điện thoại..."
                    value={filterDropdown?.q}
                    onSubmit={(text) => {
                        const updateFilterDropdown = { ...filterDropdown, q: text };
                        setFilterDropdown(updateFilterDropdown);
                    }}
                />
            </div>
            <div className={`${isInDropdown ? 'row' : 'w-100'} d-flex  flex-row  flex-wrap`}>
                {/* loc theo tram sac */}
                {!!chargePointListFilter && (
                    <div className={`${isInDropdown ? 'col-6 pr-2' : 'flex-grow-1 pr-4'} pt-1`}>
                        {UIHelper.buildFormLabel('Trạm sạc')}
                        {dropdownChargeStationEl}
                    </div>
                )}
                {/* loc theo trang thai */}
                <KTDropdownFilter
                    label='Trạng thái'
                    filterKey="status"
                    options={[
                        { name: 'Tất cả', value: '' },
                        ...ChargeHelper.Data.chargePointStatusOptions,
                    ]}
                    selectedValue={isInDropdown ? filterDropdown?.status : filter?.status}
                    isInDropdown={isInDropdown}
                    additionalClassName={`${isInDropdown ? 'pl-2 pt-2' : 'pr-0 pr-0 pr-xl-4'}`}
                    handleChange={(value) => {
                        if (isInDropdown) {
                            setFilterDropdown({ ...filterDropdown, status: value });
                        } else {
                            updateFilter('status', value);
                        }
                    }}
                />
                {/* loc theo loai tru sac */}
                <KTDropdownFilter
                    label='Loại trụ sạc'
                    filterKey="type"
                    options={[
                        { name: 'Tất cả', value: '' },
                        ...ChargeHelper.Data.chargePointTypeOptions,
                    ]}
                    selectedValue={isInDropdown ? filterDropdown?.type : filter?.type}
                    isInDropdown={isInDropdown}
                    additionalClassName={`${isInDropdown ? 'pr-2 pt-2' : 'pl-0 pr-0 pr-xl-4'}`}
                    handleChange={(value) => {
                        if (isInDropdown) {
                            setFilterDropdown({ ...filterDropdown, type: value });
                        } else {
                            updateFilter('type', value);
                        }
                    }}
                />

                {/* loc theo dong tru sac */}
                {isInDropdown && (
                    <div className="col-6 pl-2 pt-2">
                        {UIHelper.buildFormLabel('Dòng trụ sạc')}
                        <ModelTreeDropdown
                            value={filterDropdown?.modelId ?? ''}
                            brandList={brandList}
                            modelList={modelList}
                            onChange={(modelId) => {
                                setFilterDropdown({ ...filterDropdown, modelId });
                            }}
                        />
                    </div>
                )}

                {isInDropdown && (
                    <>
                        {/* cong suat */}
                        <KTDropdownFilter
                            label='Công suất'
                            filterKey="chargingSpeed"
                            options={chargingSpeedOptions}
                            selectedValue={isInDropdown ? filterDropdown?.chargingSpeed : filter?.chargingSpeed}
                            isInDropdown={isInDropdown}
                            additionalClassName={`${isInDropdown ? 'pr-2 pt-2' : 'pl-0 pr-0 pr-xl-4'}`}
                            handleChange={(value) => {
                                if (isInDropdown) {
                                    setFilterDropdown({ ...filterDropdown, chargingSpeed: value });
                                } else {
                                    updateFilter('chargingSpeed', value);
                                }
                            }}
                        />
                        {/* loc theo loai phuong tien */}
                        <KTDropdownFilter
                            label='Loại phương tiện'
                            filterKey="vehicleType"
                            options={[
                                { name: 'Tất cả', value: '' },
                                ...ChargeHelper.Data.vehicleTypeOptions,
                            ]}
                            selectedValue={isInDropdown ? filterDropdown?.vehicleType : filter?.vehicleType}
                            isInDropdown={isInDropdown}
                            additionalClassName={`${isInDropdown ? 'pl-2 pt-2' : 'pl-0 pr-0 pr-xl-4'}`}
                            handleChange={(value) => {
                                if (isInDropdown) {
                                    setFilterDropdown({ ...filterDropdown, vehicleType: value });
                                } else {
                                    updateFilter('vehicleType', value);
                                }
                            }}
                        />
                    </>
                )}
            </div>
            <div className="col-12 mb-4 px-4 mt-2"
                style={{
                    borderBottom: `1px dashed ${AppColors.gray[300]}`
                }}
            />
            <div className='d-flex gap-2 align-items-center'>
                <span style={UIHelper.buildFontStyle('700', '13px', AppColors.gray[800])}>
                    Xem trụ sạc test
                </span>
                {UIHelper.buildToggleSwitch(
                    filterDropdown.isTestChargePoint,
                    () => {
                        setFilterDropdown({ ...filterDropdown, isTestChargePoint: Number(!filterDropdown.isTestChargePoint) });
                    }
                )}
            </div>
        </div>
    );
}

export default ChargePointListFilterView;
